import { useMemo } from "react";

export const useSummaryData = (paymentParameters, transactionIds) => {
  // Helper function to round to 2 decimal places
  const roundToTwo = (num) => Math.round((num + Number.EPSILON) * 100) / 100;

  // Calculate summary data for the Summary component
  const summaryData = useMemo(() => {
    // No data at all
    if (!paymentParameters) {
      return {
        invoiceTotal: 0,
        invoiceCount: 0,
        creditNoteTotal: 0,
        creditNoteCount: 0,
        totalToPay: 0,
        creditOnAccount: 0,
      };
    }

    const hasStatement = !!paymentParameters.statement?.invoiceGroups;

    // PAYMENT-REQUEST FLOW (statement + transactionIds) – full invoice-based summary
    if (hasStatement && transactionIds && transactionIds.length > 0) {
      const selectedSet = new Set(transactionIds);

      const invoiceTotal = roundToTwo(
        paymentParameters.statement.invoiceGroups
          .flatMap((g) => g.invoices)
          .filter((inv) => selectedSet.has(inv.ledgerTransactionId))
          .reduce((sum, inv) => sum + inv.remainingAmount, 0)
      );

      const invoiceCount = paymentParameters.statement.invoiceGroups
        .flatMap((g) => g.invoices)
        .filter((inv) => selectedSet.has(inv.ledgerTransactionId)).length;

      // Calculate credit note total
      const selectedInvoices = paymentParameters.statement.invoiceGroups
        .flatMap((g) => g.invoices)
        .filter((inv) => selectedSet.has(inv.ledgerTransactionId));

      // Sum credit notes from selected invoices
      const nestedCreditNotes = selectedInvoices
        .flatMap((inv) => inv.creditNotes || [])
        .reduce((sum, cn) => sum + cn.remainingAmount, 0);

      // Sum top-level credit notes
      const topLevelCreditNotes = paymentParameters.statement.invoiceGroups
        .flatMap((g) => g.creditNotes)
        .filter((cn) => selectedSet.has(cn.ledgerTransactionId))
        .reduce((sum, cn) => sum + cn.remainingAmount, 0);

      const creditNoteTotal = roundToTwo(
        nestedCreditNotes + topLevelCreditNotes
      );

      // Calculate credit note count
      const nestedCreditNoteCount = selectedInvoices.flatMap(
        (inv) => inv.creditNotes || []
      ).length;

      const topLevelCreditNoteCount = paymentParameters.statement.invoiceGroups
        .flatMap((g) => g.creditNotes)
        .filter((cn) => selectedSet.has(cn.ledgerTransactionId)).length;

      const creditNoteCount = nestedCreditNoteCount + topLevelCreditNoteCount;

      const totalToPay = roundToTwo(
        invoiceTotal +
          creditNoteTotal +
          (paymentParameters.statement.creditOnAccount || 0)
      );

      return {
        invoiceTotal,
        invoiceCount,
        creditNoteTotal,
        creditNoteCount,
        totalToPay,
        creditOnAccount: paymentParameters.statement.creditOnAccount || 0,
      };
    }

    // PAYMENT-REQUEST FLOW (statement but no selection yet) – zeros but preserve credit-on-account
    if (hasStatement) {
      return {
        invoiceTotal: 0,
        invoiceCount: 0,
        creditNoteTotal: 0,
        creditNoteCount: 0,
        totalToPay: 0,
        creditOnAccount: paymentParameters.statement.creditOnAccount || 0,
      };
    }

    // NO STATEMENT (checkout flow or payment request without statement) – simple one-line summary
    if (typeof paymentParameters.amountRequested === "number") {
      const total = roundToTwo(paymentParameters.amountRequested);
      return {
        invoiceTotal: total,
        invoiceCount: 1,
        creditNoteTotal: 0,
        creditNoteCount: 0,
        totalToPay: total,
        creditOnAccount: 0,
      };
    }

    // Fallback – no statement and no total
    return {
      invoiceTotal: 0,
      invoiceCount: 0,
      creditNoteTotal: 0,
      creditNoteCount: 0,
      totalToPay: 0,
      creditOnAccount: 0,
    };
  }, [paymentParameters, transactionIds]);

  return summaryData;
};
